import React from "react";
import { observer } from "mobx-react";
import {
  useAwards,
  useCareerClimbsContent,
  useCoursesContent,
} from "@/hooks/useCMSData";
import { getCompletedAwards, hasCompletedClimb } from "@/utils/awards-utils";
import { Box, Typography, Card, CardContent, Grid, Chip } from "@mui/material";
import { Link } from "react-router-dom";
import { getClimbsForAward } from "../../utils/awards-utils";
import { getLadderStatus } from "@/utils/ladder-progress";
import { useGetMyAwards } from "../../hooks/useAwards";
import { useGetMyCourseCompletions } from "../../hooks/useCourseCompletions";
import { useMyProgress } from "@/hooks/useMyProgress";

const ClimbLink = ({ climb }) => {
  const { allCareerClimbs: careerClimbs } = useCareerClimbsContent();
  const { ladderProgress } = useMyProgress();

  const climbContent = careerClimbs.find((c) => String(c.id) === String(climb));
  const isCompleted = hasCompletedClimb(climb, careerClimbs, ladderProgress);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 1,
        m: 0.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
        }}
      >
        <Chip
          sx={{ cursor: "pointer" }}
          label={`${isCompleted ? "✅" : "❌"} ${climbContent?.name} ${climb}`}
          target="_blank"
          component={Link}
          to={`/career-climbs/${climb}`}
        />
      </Box>

      {climbContent?.ladders?.map((ladder) => {
        const isCompleted =
          getLadderStatus(ladder, ladderProgress) === "COMPLETED";
        return (
          <div style={{ fontSize: "0.8rem" }} key={ladder.id}>
            {isCompleted ? "✅" : "❌"} {ladder.name} {ladder.id}
          </div>
        );
      })}
    </Box>
  );
};

const AwardedStatus = ({ award, myAwards }) => {
  const isAwarded = myAwards.find((a) => a.awardID === award.id)
    ? "⭐ Awarded"
    : "Not Awarded";
  return <span>Awarded in DB: {isAwarded}</span>;
};

const CourseCompletedStatus = ({ course, myCourseCompletions }) => {
  const isCompleted = myCourseCompletions.find(
    (c) => String(c.courseID) === String(course.id),
  )
    ? "⭐ Completed"
    : "Not Completed";
  return <span>Completed in DB: {isCompleted}</span>;
};

export const AwardsDebug = observer(() => {
  const { ladderProgress } = useMyProgress();
  const { data: myAwards } = useGetMyAwards();
  const { data: myCourseCompletions } = useGetMyCourseCompletions();

  const { awards, loading: awardsLoading, error: awardsError } = useAwards();
  const { allCareerClimbs: careerClimbs, loading: careerClimbsLoading } =
    useCareerClimbsContent();
  const { allCourses: courses, loading: coursesLoading } = useCoursesContent();

  if (awardsLoading || careerClimbsLoading || coursesLoading) {
    return <Typography>Loading...</Typography>;
  }

  if (awardsError) {
    return (
      <Typography color="error">
        Error loading awards: {awardsError.message}
      </Typography>
    );
  }

  const completedAwards = getCompletedAwards(
    awards,
    careerClimbs,
    ladderProgress,
    courses,
  );

  // Check course completion status
  const courseCompletionStatus = courses.map((course) => {
    const climbs = course.climbs || [];
    const allClimbsCompleted =
      climbs.length > 0 &&
      climbs.every((climb) =>
        hasCompletedClimb(climb.id, careerClimbs, ladderProgress),
      );
    return { course, allClimbsCompleted };
  });

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Awards & Courses Debug Page
      </Typography>

      <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
        Course Completions ({myCourseCompletions.length} saved)
      </Typography>

      <Grid container spacing={3}>
        {courseCompletionStatus.map(({ course, allClimbsCompleted }) => (
          <Grid item xs={12} sm={6} key={course.id}>
            <Card
              sx={{
                height: "100%",
                bgcolor: allClimbsCompleted
                  ? "success.dark"
                  : "background.paper",
                color: allClimbsCompleted ? "white" : "text.primary",
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {course.name} (# {course.id})
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Product Plan:{" "}
                  {course.productPlan?.map((p) => p.code).join(", ") || "none"}
                </Typography>
                <Typography variant="body1" sx={{ mt: 2 }} gutterBottom>
                  All Climbs Completed: {allClimbsCompleted ? "Yes" : "No"}{" "}
                  <br />
                  <CourseCompletedStatus
                    course={course}
                    myCourseCompletions={myCourseCompletions}
                  />
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Climbs ({course.climbs?.length || 0}):
                </Typography>
                {course.climbs?.map((climb) => (
                  <ClimbLink key={climb.id} climb={climb.id} />
                ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h5" gutterBottom sx={{ mt: 5 }}>
        Awards ({myAwards.length} saved)
      </Typography>

      <Grid container spacing={3}>
        {awards.map((award) => {
          const isCompleted = completedAwards.includes(award);
          return (
            <Grid item xs={12} sm={6} key={award.id}>
              <Card
                sx={{
                  height: "100%",
                  bgcolor: isCompleted ? "success.dark" : "background.paper",
                  color: isCompleted ? "white" : "text.primary",
                }}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {award.name} (# {award.id})
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    Type: {award.awardType}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    Rule: {award.awardRule}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    Product Plan:{" "}
                    {award.productPlan?.map((p) => p.code).join(", ")}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2 }} gutterBottom>
                    Completion Status:{" "}
                    {isCompleted ? "Completed" : "Not Completed"} <br />
                    <AwardedStatus award={award} myAwards={myAwards} />
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    Required Climbs (
                    {getClimbsForAward(award, careerClimbs, courses)?.length}):
                  </Typography>
                  {getClimbsForAward(award, careerClimbs, courses)?.map(
                    (climb) => (
                      <ClimbLink key={climb} climb={climb} />
                    ),
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
});

export default AwardsDebug;
